import React, { useEffect } from 'react'
import MCBanner from '@mc/banner'
import { usePrimaryCta } from '@mc/growth'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { usePromotion } from '@mc/promo-utils'
import { getPageSource, track } from '@mc/track-event'
import { Pen7233CountdownWrapper } from '../../experiments/pen-7233_v1_promo_banner_countdown'
import {
  Pen7565BannerTitleWrapper,
  useTrackPen7565ExperimentViewed,
} from '../../experiments/pen-7565_v1_promo_banner_200_classes'
import {
  Pen7584BannerTitleWrapper,
  useTrackPen7584ExperimentViewed,
} from '../../experiments/pen-7584_v1_promo_banner_all_classes'
import {
  PEN_7680_GIFT_CTA_ACTION,
  PEN_7680_GIFT_CTA_HREF,
  Pen7680BannerCtaWrapper,
  usePen7680Experiment,
  useTrackPen7680ExperimentViewed,
} from '../../experiments/pen-7680_v1_promo_banner_gift_cta'
import {
  usePlanZFreeGiftExperiment,
  BannerRedesign,
} from '../../experiments/prcng-955_v1_plan_z_free_gift'
import { i18nInstance } from '../../i18n'
import { PROMO_I18N_NAMESPACE } from '../../lib/constants'
import { trackBannerCTAClick, trackBannerOnClose } from '../../lib/events'
import { UrgencyText } from '../featureVariables'
import { BannerContent } from './BannerContent'
import { BannerPromoText } from './BannerPromoText'
import { getBannerPropsByPath } from './getPathBannerProps'
import classes from './Banner.module.scss'

const BannerProvided = withI18nContext(i18nInstance)(({
  pathname,
  isGift,
}: BannerProps) => {
  const { t } = useTranslation(PROMO_I18N_NAMESPACE)
  const { ctaHref, ctaCopy } = usePrimaryCta()
  const { isDark, hideCta } = getBannerPropsByPath(pathname)

  const { isVariant: isPlanZFreeGift, trackEV: trackPlanZFreeGiftViewed } =
    usePlanZFreeGiftExperiment({ isGift })

  useTrackPen7565ExperimentViewed()
  useTrackPen7584ExperimentViewed()

  const { isEligible: isPen7680Eligible, isVariant: isPen7680Variant } =
    usePen7680Experiment({ hideCta })
  useTrackPen7680ExperimentViewed(isPen7680Eligible)

  const onClose = () => trackBannerOnClose()
  const onCtaClick = () => {
    trackBannerCTAClick({ action: ctaCopy, href: ctaHref })
  }
  const onGiftClick = () => {
    trackBannerCTAClick({
      action: PEN_7680_GIFT_CTA_ACTION,
      href: PEN_7680_GIFT_CTA_HREF,
    })
  }

  useEffect(() => {
    track('Promo Banner Viewed', {
      page_source: getPageSource(),
    })
    trackPlanZFreeGiftViewed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MCBanner
      theme={isDark ? 'dark' : 'light'}
      isClosable
      onClose={onClose}
      aria-label={t('common.promoBanner.a11y')}
      closeBtnClassName={classes.close}
      className='mc-mb-2'
    >
      {isPlanZFreeGift ? (
        <BannerContent>
          <BannerRedesign
            hideCta={hideCta}
            ctaHref={ctaHref}
            onCtaClick={onCtaClick}
            ctaCopy={ctaCopy}
          />
        </BannerContent>
      ) : (
        <BannerContent>
          <BannerContent.Title>
            <Pen7584BannerTitleWrapper
              control={
                <Pen7565BannerTitleWrapper control={<BannerPromoText />} />
              }
            />
          </BannerContent.Title>

          <BannerContent.Subtitle>
            <Pen7233CountdownWrapper isGift={isGift}>
              <UrgencyText />
            </Pen7233CountdownWrapper>
          </BannerContent.Subtitle>

          {hideCta ? null : (
            <Pen7680BannerCtaWrapper
              isVariant={isPen7680Variant}
              control={
                <BannerContent.CTA ctaHref={ctaHref} onCtaClick={onCtaClick}>
                  {ctaCopy}
                </BannerContent.CTA>
              }
              ctaHref={ctaHref}
              ctaCopy={ctaCopy}
              onCtaClick={onCtaClick}
              onGiftClick={onGiftClick}
            />
          )}
        </BannerContent>
      )}
    </MCBanner>
  )
})

export type BannerProps = {
  pathname: string
  isGift?: boolean
}

export const Banner = ({ pathname, isGift }: BannerProps) => {
  const { isPromotionOn } = usePromotion({ isGift })

  if (!isPromotionOn) {
    return null
  }

  return <BannerProvided pathname={pathname} isGift={isGift} />
}
